import { useEffect, useMemo, useState } from 'react';

import { devMessage } from '../settings/settings';
import { getLangForTranslations } from '../utils/langUtil';
import { getFullApiUrl } from '../utils/navigationUtil';
import { REQUEST_STATUS } from '../utils/requestUtil';
import useReadCartExpiration from './useReadCartExpiration';
import useReadResponseDataModifier from './useReadResponseDataModifier';

// ==============================================================================================
// This hook is used to fetch data from a url (GET)
// If the url is a state and it changes, then this hook is triggered again
// We send back the data and also the status which is a value of REQUEST_STATUS
// If the data is a non empty array, we set hasMore to true, this flag is also sent back
// You can use propertyToReturn to return a specific property from the response

// If the flag replaceData is set to true, we replace the data
// Otherwise, if set to false then we append the new data to the existing fetched data
// ==============================================================================================

const useFetch = (url, propertyToReturn, refReplaceData, expectedCountResults) => {
  const CURRENT_APP_LANG = useMemo(() => getLangForTranslations(), []);

  const [data, setData] = useState(null);
  const [hasMore, setHasMore] = useState(false);
  const [status, setStatus] = useState(REQUEST_STATUS.LOADING);
  const hasExpired = useReadCartExpiration();
  const ResponseDataModifierClass = useReadResponseDataModifier(url);

  const getResponseData = data => {
    let responseData = null;
    if (!propertyToReturn) {
      devMessage('RESPONSE (data):', data);
      responseData = data;
    } else {
      devMessage('RESPONSE (prop):', data[propertyToReturn]);
      responseData = data[propertyToReturn];
    }

    if (ResponseDataModifierClass != null) {
      responseData = new ResponseDataModifierClass().updateValues(responseData);
      devMessage('RESPONSE converted by', ResponseDataModifierClass, 'to', responseData);
    }
    return responseData;
  };

  useEffect(() => {
    const fullUrl = getFullApiUrl(url);
    if (!fullUrl || hasExpired) return;
    setStatus(REQUEST_STATUS.LOADING);

    devMessage('CALL:', fullUrl);

    const replaceData = refReplaceData ? refReplaceData.current : true;
    const abortController = new AbortController();
    fetch(fullUrl, {
      credentials: 'include', // Required to store/retransmit ajax cookies (csrf/session) !!
      signal: abortController.signal,
      headers: {
        'Accept-Language': CURRENT_APP_LANG,
      },
    })
      .then(res => {
        if (!res.ok) throw Error();
        return res.json();
      })
      .then(rawData => {
        const responseData = getResponseData(rawData);

        const isArray = Array.isArray(responseData);
        setHasMore(isArray && responseData.length === expectedCountResults);

        if (replaceData) {
          setData(responseData);
        } else {
          setData(isArray ? data.concat(responseData) : data + responseData);
        }

        setStatus(REQUEST_STATUS.OK);
      })
      .catch(err => {
        if (err.name === 'AbortError') return;

        console.error(err);
        setStatus(REQUEST_STATUS.ERROR);
      });

    return () => abortController.abort();
  }, [url]);

  return { data, status, hasMore };
};

export default useFetch;
